// src/utils/parseError.js

/**
 * تحديد لغة المتصفح / التطبيق الحالية (ar أو en)
 */
export function getCurrentLanguage() {
  try {
    const lng = localStorage.getItem("i18nextLng") || localStorage.getItem("lang") || "ar";
    return lng.toLowerCase().startsWith("ar") ? "ar" : "en";
  } catch {
    return "ar";
  }
}

/**
 * استخراج النص المناسب من كائن ثنائي اللغة { ar, en }
 */
export function extractLocalizedText(obj, lang = null) {
  if (!obj) return "";
  if (typeof obj === "string") return obj.trim();
  if (typeof obj !== "object") return String(obj);

  const activeLang = lang || getCurrentLanguage();

  if (obj.ar !== undefined || obj.en !== undefined) {
    if (activeLang === "ar") {
      const arVal = typeof obj.ar === "string" ? obj.ar.trim() : extractErrorMessage(obj.ar, "ar");
      if (arVal) return arVal;
      const enVal = typeof obj.en === "string" ? obj.en.trim() : extractErrorMessage(obj.en, "en");
      if (enVal) return enVal;
    } else {
      const enVal = typeof obj.en === "string" ? obj.en.trim() : extractErrorMessage(obj.en, "en");
      if (enVal) return enVal;
      const arVal = typeof obj.ar === "string" ? obj.ar.trim() : extractErrorMessage(obj.ar, "ar");
      if (arVal) return arVal;
    }
  }

  return "";
}

/**
 * استخراج نص الخطأ أو الرسالة من أي هيكل بيانات قادم من الباك إند
 * يدعم: نصوص عادية، كائنات لغات {ar, en}، مصفوفات أخطاء، كائنات Laravel validation {errors: {field: [...]}}
 */
export function extractErrorMessage(data, lang = null) {
  if (data === null || data === undefined) return "";
  if (typeof data === "string") return data.trim();
  if (typeof data === "number" || typeof data === "boolean") return String(data);

  const activeLang = lang || getCurrentLanguage();

  // 1. إذا كان الكائن يحتوي مباشرة على مفاتيح ar أو en
  if (typeof data === "object" && (data.ar !== undefined || data.en !== undefined)) {
    const loc = extractLocalizedText(data, activeLang);
    if (loc) return loc;
  }

  // 2. إذا كان مصفوفة نصوص أو كائنات
  if (Array.isArray(data)) {
    const items = data
      .map((item) => extractErrorMessage(item, activeLang))
      .filter((x) => Boolean(x) && typeof x === "string" && x.trim());
    return items.join(" - ");
  }

  // 3. إذا كان كائناً
  if (typeof data === "object") {
    // أ) التحقق من أخطاء التحقق errors (مثل Laravel: { errors: { email: ["..."], name: { ar: "..." } } })
    const validationErrors = data.errors || data.validation_errors;
    if (validationErrors && typeof validationErrors === "object") {
      const allValidationMessages = [];
      for (const field of Object.keys(validationErrors)) {
        const fieldVal = validationErrors[field];
        const extracted = extractErrorMessage(fieldVal, activeLang);
        if (extracted) {
          allValidationMessages.push(extracted);
        }
      }
      if (allValidationMessages.length > 0) {
        return allValidationMessages.join("\n");
      }
    }

    // ب) التحقق من حقل message (الأكثر شيوعاً في ردود الـ API)
    if (data.message !== undefined && data.message !== null) {
      const msg = extractErrorMessage(data.message, activeLang);
      if (msg) return msg;
    }

    // ج) التحقق من حقل error
    if (data.error !== undefined && data.error !== null) {
      const err = extractErrorMessage(data.error, activeLang);
      if (err) return err;
    }

    // د) التحقق من حقل messages
    if (data.messages !== undefined && data.messages !== null) {
      const msgs = extractErrorMessage(data.messages, activeLang);
      if (msgs) return msgs;
    }

    // هـ) البحث داخل باقي مفاتيح الكائن باستثناء الحقول غير المتعلقة بنص الخطأ
    const ignoredKeys = new Set(["status", "code", "success", "status_code", "data"]);
    for (const key of Object.keys(data)) {
      if (ignoredKeys.has(key)) continue;
      const val = data[key];
      const res = extractErrorMessage(val, activeLang);
      if (res) return res;
    }

    // و) إذا كان هناك كائن data بداخله رسالة
    if (data.data && typeof data.data === "object") {
      const nestedDataMsg = extractErrorMessage(data.data, activeLang);
      if (nestedDataMsg) return nestedDataMsg;
    }
  }

  // Fallback آمن لمنع إرجاع أي object لريآكت
  try {
    return JSON.stringify(data);
  } catch {
    return String(data);
  }
}

/**
 * الدالة الأساسية لاستخراج خطأ الباك إند الصريح من كائن الخطأ (Axios Error وغيره)
 * تضمن:
 * 1. عدم استبدال خطأ السيرفر بنص ثابت أو رسالة "Network Error".
 * 2. إرجاع رسالة السيرفر الصريحة كما هي باللغة الصحيحة للمستخدم.
 * 3. التعامل مع كائنات {ar, en} والتحقق من عدم إرجاع أي كائن Object إطلاقاً.
 */
export function getErrorMessageFromAxiosError(err, fallback = null) {
  const activeLang = getCurrentLanguage();
  const isAr = activeLang === "ar";
  const defaultFallback = isAr ? "حدث خطأ غير متوقع" : "Something went wrong!";

  if (!err) {
    return fallback || defaultFallback;
  }

  // 1. إذا كان الخطأ نصاً مباشراً
  if (typeof err === "string" && err.trim()) {
    return err.trim();
  }

  // 2. إذا كان هناك رد صريح قادم من السيرفر (err.response.data)
  // هنا نحرص على عرض خطأ الباك إند كما هو دون استبداله بنص ثابت
  const respData = err?.response?.data;
  if (respData) {
    const serverMessage = extractErrorMessage(respData, activeLang);
    if (serverMessage && typeof serverMessage === "string" && serverMessage.trim()) {
      return serverMessage.trim();
    }
  }

  // 3. إذا لم يصل رد من السيرفر أصلاً (انقطاع حقيقي في الإنترنت أو تعذر الوصول إلى الخادم)
  if (!err.response) {
    if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
      return isAr
        ? "تعذر الاتصال بالخادم، يرجى التحقق من اتصالك بالإنترنت"
        : "Cannot connect to server, please check your internet connection";
    }
    if (err.message && typeof err.message === "string" && err.message.trim()) {
      return err.message.trim();
    }
  }

  // 4. فحص كائن الخطأ نفسه
  const directMessage = extractErrorMessage(err, activeLang);
  if (directMessage && typeof directMessage === "string" && directMessage.trim()) {
    return directMessage.trim();
  }

  return fallback || defaultFallback;
}

/**
 * دالة مساعدة لمعالجة رسائل النجاح أو أي نصوص معروضة في الـ Toast
 * تضمن إرجاع String دائماً وتحويل أي كائن {ar, en} لنص باللغة الحالية
 */
export function parseResponseMsg(msg, fallback = "") {
  if (!msg) return fallback;
  if (typeof msg === "string") return msg.trim();
  const activeLang = getCurrentLanguage();
  const extracted = extractErrorMessage(msg, activeLang);
  return extracted || fallback || (activeLang === "ar" ? "تمت العملية بنجاح" : "Operation completed successfully");
}

export default getErrorMessageFromAxiosError;
